import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import ManageiPadForm from './ManageiPadForm';
import { useAxios, APIError } from '../../core/_axios';
import { Ipad } from '../../core/_models';
import { getIpadByUniqueIdentifier } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import { SetupIpadDeviceForm } from './SetupIpadDeviceForm';
import { ManageServiceForm } from './ManageServiceForm';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import { useAuth } from '../../AuthContext';
import useAccount from '../../hooks/useAccount';
import { isPaymentOutOfDate, getAgencyName } from '../../utils/paymentUtils';

export function ManageIpadRoutes() {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();
    const { currentUser, viewingAllAccounts } = useAuth();
    const { account } = useAccount({
        axios,
        isForCurrentAccount: !viewingAllAccounts
    });

    const {
        pathname
    } = useLocation();

    const params = useParams();

    usePageTitle(copy.IPads.manage.title);

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [ipad, setIpad] = useState<Ipad | null>(null);

    const loadiPad = useCallback(async () => {
        if (!params.ipadUniqueId) {
            setLoading(false);
            setLoadingError(copy.Common.messages.errorOccurred);
            return;
        }
        setLoading(true);
        setLoadingError(null);
        try {
            const updatedIpad = await getIpadByUniqueIdentifier(axios, params.ipadUniqueId);
            setIpad(updatedIpad);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, params.ipadUniqueId]);

    useEffect(() => {
        loadiPad();
    }, [loadiPad]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadiPad}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </div>
        );
    }

    // Block access if payment is out of date
    const paymentOutOfDate = isPaymentOutOfDate(currentUser, account);

    if (paymentOutOfDate) {
        const agencyName = getAgencyName(currentUser, account);
        return (
            <div className="container-fixed">
                <div className="card">
                    <div className="card-body">
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <i className="ki-filled ki-information-circle text-6xl text-red-600 mb-4"></i>
                            <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                                Feature Restricted
                            </h3>
                            <p className="text-gray-600 mb-4 max-w-md">
                                Managing iPads is currently restricted because payment for {agencyName} is past due.
                            </p>
                            <p className="text-sm text-gray-500">
                                Please contact your agency administrator or SGB support to resolve this issue.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // if (!ipad.public_data)

    return <>
        <div className="container-fixed">
            <div className={CONTAINER_CONTENT_CLASS}>
                <div className="flex flex-col justify-center gap-2">
                    <h1 className="text-xl font-semibold leading-none text-gray-900">
                        {copy.IPads.manage.title}
                    </h1>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                        {/* subtitle? */}
                    </div>
                </div>
                {false && <div className="flex items-center gap-2.5">
                    <a className="btn btn-sm btn-light" href="#">
                        Go to Teams
                    </a>
                </div>}
            </div>
        </div>
        <div className="container-fixed">
            <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link to={`/ipads/${ipad.unique_identifier}`}
                            className={[
                                pathname === `/ipads/${ipad.unique_identifier}/details` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            {copy.IPads.manage.details}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/ipads/${ipad.unique_identifier}/setup-device`}
                            className={[
                                pathname === `/ipads/${ipad.unique_identifier}/setup-device` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            {copy.IPads.setup.title}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/ipads/${ipad.unique_identifier}/manage-service`}
                            className={[
                                pathname === `/ipads/${ipad.unique_identifier}/manage-service` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            {ipad.active_service ? copy.IPads.manage.endService : copy.IPads.manage.sendService}
                        </Link>
                    </li>
                </ul>
            </div>
            <Routes>
                <Route path="/details" element={
                    <ManageiPadForm
                        iPad={ipad}
                        onSuccessfullyUpdatedIpad={(iPad: Ipad) => {
                            console.log('service: ', iPad)
                        }}
                    />}
                />
                <Route path="/setup-device" element={
                    <SetupIpadDeviceForm
                        iPad={ipad}
                    />}
                />
                <Route path="/manage-service" element={
                    <ManageServiceForm
                        iPad={ipad}
                        setIpad={setIpad}
                    />}
                />
                <Route
                    path="*"
                    element={<Navigate
                        to={`/ipads/${ipad.unique_identifier}/details`}
                        replace
                    />}
                />
            </Routes>
        </div>
    </>
}